import api from './api';
import { unwrapList, unwrapObject, type ListResult } from './api-envelope';

export interface ProductExportAttrColumn {
  code: string;
  name: string;
  data_type: string;
  enumLabels?: Record<string, string>;
}

export interface ProductExportData {
  products: Array<Record<string, unknown>>;
  variantsByProduct: Record<string, Array<Record<string, unknown>>>;
  attrColumns: ProductExportAttrColumn[];
}
import type { ProductListQuery } from './queryKeys';
import type {
  ProductTenantType,
  CreateProductInput,
  UpdateProductInput,
  VariantUpsertInput,
} from '@erpgold/shared';
import { type ProductState } from '@erpgold/shared';

export interface VariantSearchResult {
  _id: string;
  variant_code: string;
  product_id: string;
  product_name: string;
  product_sku: string;
  tracking_mode: 'quantity' | 'serial';
  attrs: Record<string, unknown>;
  is_default: boolean;
  is_sellable: boolean;
  is_published: boolean;
  cached_price?: number;
}

export interface VariantSearchResponse {
  data: VariantSearchResult[];
  total: number;
}

// Strip fields that the backend CreateVariantDto rejects via whitelist validation:
// - cached_price / cached_buy_price / cached_price_at: FE-only preview fields
// - product_id / tenant_id / created_at / updated_at / __v / cached_buy_price_at:
//   server-managed Mongoose document metadata leaked from GET responses
//   (relevant when editing/cloning where variants are loaded from BE)
function sanitizeVariant(v: VariantUpsertInput): VariantUpsertInput {
  const {
    cached_price: _cp,
    cached_buy_price: _cbp,
    cached_price_at: _cpa,
    cached_buy_price_at: _cbpa,
    product_id: _pid,
    tenant_id: _tid,
    created_at: _ca,
    updated_at: _ua,
    __v: _v,
    ...clean
  } = v as VariantUpsertInput & {
    cached_price?: unknown;
    cached_buy_price?: unknown;
    cached_price_at?: unknown;
    cached_buy_price_at?: unknown;
    product_id?: unknown;
    tenant_id?: unknown;
    created_at?: unknown;
    updated_at?: unknown;
    __v?: unknown;
  };
  return clean as VariantUpsertInput;
}

function sanitizeBody<T extends { variants?: VariantUpsertInput[] }>(body: T): T {
  if (!body.variants?.length) return body;
  return { ...body, variants: body.variants.map(sanitizeVariant) };
}

export const productService = {
  list: (params: ProductListQuery): Promise<ListResult<ProductTenantType>> =>
    api.get('/products', { params }).then(unwrapList<ProductTenantType>),

  getById: (id: string): Promise<ProductTenantType> =>
    api.get(`/products/${id}`).then(unwrapObject<ProductTenantType>),

  create: (body: CreateProductInput): Promise<ProductTenantType> =>
    api.post('/products', sanitizeBody(body)).then(unwrapObject<ProductTenantType>),

  update: (id: string, body: UpdateProductInput): Promise<ProductTenantType> =>
    api.put(`/products/${id}`, sanitizeBody(body)).then(unwrapObject<ProductTenantType>),

  updateState: (id: string, state: ProductState): Promise<ProductTenantType> =>
    api.patch(`/products/${id}/state`, { state }).then(unwrapObject<ProductTenantType>),

  remove: (id: string): Promise<void> =>
    api.delete(`/products/${id}`).then(() => undefined),

  clone: (id: string): Promise<{ _id: string; name: string }> =>
    api.post(`/products/${id}/clone`).then(unwrapObject<{ _id: string; name: string }>),

  bulkUpdateState: (ids: string[], state: ProductState): Promise<{ updated: number }> =>
    api.patch('/products/bulk-state', { ids, state }).then(unwrapObject<{ updated: number }>),

  exportList: (params: Omit<ProductListQuery, 'page' | 'page_size'>): Promise<ProductExportData> =>
    api.get('/products/export', { params }).then(unwrapObject<ProductExportData>),

  variantSearch: (params: { q?: string; limit?: number; include_hidden?: boolean }): Promise<VariantSearchResponse> =>
    api.get('/variants/search', { params }).then((r) => r.data),
};
